// src/components/employee/SelfReviewModal.js
import React, { useState, useEffect } from 'react';
import { X, Save, Send, Calendar, User, Star, AlertCircle, Loader } from 'lucide-react';
import performanceService from '../../services/performance.service';

const SelfReviewModal = ({ isOpen, onClose, review, mode }) => {
    const [formData, setFormData] = useState({
        template_id: '',
        review_cycle: 'monthly',
        review_period_start: '',
        review_period_end: '',
        overall_rating: '',
        technical_skills: '',
        communication_skills: '',
        teamwork: '',
        problem_solving: '',
        leadership: '',
        strengths: '',
        areas_for_improvement: '',
        goals: '',
        comments: '',
        reviewer_id: ''
    });

    const [template, setTemplate] = useState(null);
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState(null);

    const isViewMode = mode === 'view';
    const isEditMode = mode === 'edit';

    useEffect(() => {
        if (isOpen) {
            if (review) {
                // Populate form with existing review data
                setFormData({
                    template_id: review.template_id || '',
                    review_cycle: review.review_cycle || 'monthly',
                    review_period_start: review.review_period_start?.split('T')[0] || '',
                    review_period_end: review.review_period_end?.split('T')[0] || '',
                    overall_rating: review.overall_rating || '',
                    technical_skills: review.technical_skills || '',
                    communication_skills: review.communication_skills || '',
                    teamwork: review.teamwork || '',
                    problem_solving: review.problem_solving || '',
                    leadership: review.leadership || '',
                    strengths: review.strengths || '',
                    areas_for_improvement: review.areas_for_improvement || '',
                    goals: review.goals || '',
                    comments: review.comments || '',
                    reviewer_id: review.reviewer_id || ''
                });
            }
            fetchInitialData();
        }
    }, [isOpen, review]);

    // Calculate overall rating as average of skill ratings
    useEffect(() => {
        const skills = [
            parseFloat(formData.technical_skills) || 0,
            parseFloat(formData.communication_skills) || 0,
            parseFloat(formData.teamwork) || 0,
            parseFloat(formData.problem_solving) || 0,
            parseFloat(formData.leadership) || 0
        ].filter(v => v > 0);

        if (skills.length > 0) {
            const avg = skills.reduce((sum, val) => sum + val, 0) / skills.length;
            setFormData(prev => ({ ...prev, overall_rating: avg.toFixed(1) }));
        }
    }, [
        formData.technical_skills,
        formData.communication_skills,
        formData.teamwork,
        formData.problem_solving,
        formData.leadership
    ]);

    const fetchInitialData = async () => {
        try {
            setLoadingData(true);
            setError(null);

            // Fetch managers
            const managersData = await performanceService.getMyManagers();
            setManagers(managersData);

            // Fetch template only if creating new review (not editing)
            if (!review?.template_id) {
                const templateData = await performanceService.getMyTemplate();
                console.log('📋 Template data received:', templateData);
                if (templateData) {
                    setTemplate(templateData);
                    setFormData(prev => ({ ...prev, template_id: templateData.id }));
                } else {
                    console.warn('⚠️ No template assigned to employee');
                }
            }
        } catch (err) {
            console.error('Error fetching initial data:', err);
            setError('Failed to load template and managers');
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveDraft = async () => {
        try {
            setLoading(true);
            setError(null);

            const submitData = {
                ...formData,
                status: 'draft'
            };

            if (review) {
                await performanceService.updateSelfReview(review.id, submitData);
            } else {
                await performanceService.createSelfReview(submitData);
            }

            onClose();
        } catch (err) {
            console.error('Error saving draft:', err);
            setError(err.response?.data?.error || 'Failed to save draft');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);

            // Validation - check each field individually to provide better error messages
            const missingFields = [];
            if (!formData.template_id) missingFields.push('performance template (please contact admin to assign one)');
            if (!formData.review_cycle) missingFields.push('review cycle');
            if (!formData.review_period_start) missingFields.push('review period start date');
            if (!formData.review_period_end) missingFields.push('review period end date');
            if (!formData.reviewer_id) {
                if (managers.length === 0) {
                    missingFields.push('manager (no managers with portal access found - please contact admin)');
                } else {
                    missingFields.push('manager (please select from dropdown)');
                }
            }

            if (missingFields.length > 0) {
                setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
                setLoading(false);
                return;
            }

            console.log('📤 Submitting review data:', { ...formData, status: 'completed' });

            const submitData = {
                ...formData,
                status: 'completed'
            };

            if (review) {
                await performanceService.updateSelfReview(review.id, submitData);
            } else {
                await performanceService.createSelfReview(submitData);
            }

            onClose();
        } catch (err) {
            console.error('Error submitting review:', err);
            setError(err.response?.data?.error || 'Failed to submit review');
        } finally {
            setLoading(false);
        }
    };

    const renderStarRating = (name, value, label) => {
        const numValue = parseFloat(value) || 0;
        const percentage = (numValue / 5) * 100;

        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}
                    </label>
                    <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {value || '0'} / 5.0
                    </span>
                </div>

                {/* Star buttons */}
                <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                            key={rating}
                            type="button"
                            disabled={isViewMode}
                            onClick={() => setFormData(prev => ({ ...prev, [name]: rating.toString() }))}
                            className={`p-1.5 rounded-lg transition-all ${
                                isViewMode ? 'cursor-default' : 'hover:bg-gray-200 dark:hover:bg-gray-600 hover:scale-110'
                            }`}
                        >
                            <Star
                                className={`w-7 h-7 transition-all ${
                                    numValue >= rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-300 dark:text-gray-600'
                                }`}
                            />
                        </button>
                    ))}
                </div>

                {/* Slider */}
                {!isViewMode && (
                    <div className="mb-3">
                        <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.1"
                            value={value || 0}
                            onChange={(e) => setFormData(prev => ({ ...prev, [name]: e.target.value }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-blue-600"
                            style={{
                                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #d1d5db ${percentage}%, #d1d5db 100%)`
                            }}
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span>Poor</span>
                            <span>Fair</span>
                            <span>Good</span>
                            <span>Very Good</span>
                            <span>Excellent</span>
                        </div>
                    </div>
                )}

                {/* Visual progress bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                    <div
                        className={`h-2.5 rounded-full transition-all duration-300 ${
                            numValue >= 4.5 ? 'bg-green-600' :
                            numValue >= 3.5 ? 'bg-blue-600' :
                            numValue >= 2.5 ? 'bg-yellow-600' :
                            numValue > 0 ? 'bg-orange-600' : 'bg-gray-400'
                        }`}
                        style={{ width: `${percentage}%` }}
                    />
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {isViewMode ? 'View' : isEditMode ? 'Edit' : 'Create'} Self Review
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loadingData ? (
                        <div className="flex items-center justify-center h-64">
                            <Loader className="w-8 h-8 animate-spin text-blue-600" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                                </div>
                            )}

                            {/* Review Cycle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    Review Cycle *
                                </label>
                                <select
                                    name="review_cycle"
                                    value={formData.review_cycle}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                >
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="annual">Annual</option>
                                </select>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    You can submit one {formData.review_cycle} review per period
                                </p>
                            </div>

                            {/* Review Period */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Review Period Start *
                                    </label>
                                    <input
                                        type="date"
                                        name="review_period_start"
                                        value={formData.review_period_start}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Review Period End *
                                    </label>
                                    <input
                                        type="date"
                                        name="review_period_end"
                                        value={formData.review_period_end}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>
                            </div>

                            {/* Select Manager */}
                            {!isViewMode && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        <User className="w-4 h-4 inline mr-1" />
                                        Submit to Manager *
                                    </label>
                                    <select
                                        name="reviewer_id"
                                        value={formData.reviewer_id}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select a manager</option>
                                        {managers.map((manager) => (
                                            <option key={manager.id} value={manager.id}>
                                                {manager.first_name} {manager.last_name} - {manager.manager_level} ({manager.designation})
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Select the manager who will review your self-assessment
                                    </p>
                                </div>
                            )}

                            {/* Skill Ratings */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                    Skill Ratings
                                </h3>

                                {renderStarRating('technical_skills', formData.technical_skills, 'Technical Skills')}
                                {renderStarRating('communication_skills', formData.communication_skills, 'Communication Skills')}
                                {renderStarRating('teamwork', formData.teamwork, 'Teamwork')}
                                {renderStarRating('problem_solving', formData.problem_solving, 'Problem Solving')}
                                {renderStarRating('leadership', formData.leadership, 'Leadership')}

                                {/* Overall Rating (Auto-calculated) */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                            Overall Rating (Average)
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                            <span className="text-lg font-bold text-blue-900 dark:text-blue-300">
                                                {formData.overall_rating || '0.0'} / 5.0
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Text Fields */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                    Self-Assessment
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Strengths
                                    </label>
                                    <textarea
                                        name="strengths"
                                        value={formData.strengths}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        rows="3"
                                        placeholder="What are your key strengths demonstrated during this period?"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Areas for Improvement
                                    </label>
                                    <textarea
                                        name="areas_for_improvement"
                                        value={formData.areas_for_improvement}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        rows="3"
                                        placeholder="What areas would you like to improve?"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Goals for Next Period
                                    </label>
                                    <textarea
                                        name="goals"
                                        value={formData.goals}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        rows="3"
                                        placeholder="What are your goals for the upcoming period?"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Additional Comments
                                    </label>
                                    <textarea
                                        name="comments"
                                        value={formData.comments}
                                        onChange={handleInputChange}
                                        disabled={isViewMode}
                                        rows="3"
                                        placeholder="Any other comments or feedback you'd like to share?"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!isViewMode && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveDraft}
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Draft
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4 mr-2" />
                            )}
                            Submit Review
                        </button>
                    </div>
                )}

                {isViewMode && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelfReviewModal;
